import type { BaseEvent } from '@ag-ui/client'
import {
  answerOf,
  type ChatAnswer,
  type ChatCard,
  type ChatPart,
  type ChatTurn,
  madeBy,
  shownInput,
  shownOutput
} from '@k2/agents/chat/parts'

export type { ChatImage } from '@k2/agents/chat/parts'

// `answering`: a card the user just answered, until the run resumed by that answer returns its result.
export type TimedPart = ChatPart & { readonly startedAt?: number; readonly endedAt?: number; readonly answering?: boolean }

export type AgentTurn = {
  readonly id: string
  readonly role: 'agent'
  readonly parts: readonly TimedPart[]
  readonly status?: 'running' | 'stopped' | 'failed'
  readonly error?: string
  readonly startedAt?: number
  readonly endedAt?: number
  readonly seconds?: number
  readonly stopped?: boolean
  readonly suggestions?: readonly string[]
}

export type ShellTurn = Extract<ChatTurn, { role: 'user' }> | AgentTurn

export type ToolPart = Extract<ChatPart, { kind: 'tool' }>
export type CardPart = Extract<ChatPart, { kind: 'card' }>

type Event = BaseEvent & Record<string, unknown>

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null && !Array.isArray(value)

const isCard = (value: unknown): value is ChatCard => isRecord(value) && typeof value.kind === 'string'

const parsed = (text: string): unknown => {
  try {
    return JSON.parse(text)
  } catch {
    return text
  }
}

const upsert = (turn: AgentTurn, id: string, next: (part: TimedPart | undefined) => TimedPart) => {
  const at = turn.parts.findIndex(part => part.id === id)
  const part = next(turn.parts[at])
  return { ...turn, parts: at < 0 ? [...turn.parts, part] : turn.parts.map((entry, index) => (index === at ? part : entry)) }
}

const update = (turn: AgentTurn, id: string, change: (part: TimedPart) => TimedPart) =>
  turn.parts.some(part => part.id === id) ? upsert(turn, id, part => change(part as TimedPart)) : turn

const appendText = (turn: AgentTurn, kind: 'text' | 'reasoning', id: string, delta: string, now: number) =>
  upsert(turn, id, part => (part?.kind === kind ? { ...part, text: part.text + delta } : { kind, id, text: delta, startedAt: now }))

// The arguments of a tool call stream in as text in `args`; they become its input once the call ends.
export const applyEvent = (turn: AgentTurn, event: Event, args: Map<string, string>, now = Date.now()): AgentTurn => {
  const id = (key: string) => String(event[key] ?? '')
  switch (event.type) {
    case 'TEXT_MESSAGE_START':
      return upsert(turn, id('messageId'), part => part ?? { kind: 'text', id: id('messageId'), text: '', startedAt: now })
    case 'TEXT_MESSAGE_CONTENT':
    case 'TEXT_MESSAGE_CHUNK':
      return appendText(turn, 'text', id('messageId'), String(event.delta ?? ''), now)
    case 'REASONING_MESSAGE_START':
      return upsert(turn, id('messageId'), part => part ?? { kind: 'reasoning', id: id('messageId'), text: '', startedAt: now })
    case 'REASONING_MESSAGE_CONTENT':
    case 'REASONING_MESSAGE_CHUNK':
      return appendText(turn, 'reasoning', id('messageId'), String(event.delta ?? ''), now)
    case 'TEXT_MESSAGE_END':
    case 'REASONING_MESSAGE_END':
      return update(turn, id('messageId'), part => ({ ...part, endedAt: now }))
    case 'TOOL_CALL_START':
      args.set(id('toolCallId'), '')
      return upsert(
        turn,
        id('toolCallId'),
        part => part ?? { kind: 'tool', id: id('toolCallId'), name: id('toolCallName'), input: {}, done: false, startedAt: now }
      )
    case 'TOOL_CALL_ARGS':
      args.set(id('toolCallId'), (args.get(id('toolCallId')) ?? '') + String(event.delta ?? ''))
      return turn
    case 'TOOL_CALL_END':
      return update(turn, id('toolCallId'), part =>
        part.kind === 'tool' ? { ...part, input: shownInput(parsed(args.get(part.id) || '{}')) } : part
      )
    case 'TOOL_CALL_RESULT': {
      const output = parsed(String(event.content ?? ''))
      return upsert(turn, id('toolCallId'), part => {
        const name = part?.kind === 'tool' || part?.kind === 'card' ? part.name : ''
        if (isRecord(output) && isCard(output.card))
          return {
            kind: 'card',
            id: id('toolCallId'),
            name,
            card: output.card,
            answer: answerOf(output),
            interruptId: null,
            ...madeBy(output)
          }
        if (part?.kind === 'card') return part
        return {
          kind: 'tool',
          id: id('toolCallId'),
          name,
          input: part?.kind === 'tool' ? part.input : {},
          output: shownOutput(output),
          done: true,
          startedAt: part?.startedAt,
          endedAt: now
        }
      })
    }
    case 'RUN_FINISHED': {
      const outcome = event.outcome as { type?: string; interrupts?: { id: string; toolCallId?: string; metadata?: unknown }[] } | undefined
      const finished: AgentTurn = { ...turn, endedAt: now }
      if (outcome?.type !== 'interrupt') return finished
      return (outcome.interrupts ?? []).reduce((next, interrupt) => {
        const mastra = isRecord(interrupt.metadata) && isRecord(interrupt.metadata.mastra) ? interrupt.metadata.mastra : {}
        const card = mastra.suspendPayload
        const toolCallId = interrupt.toolCallId
        if (!toolCallId || !isCard(card)) return next
        return upsert(next, toolCallId, part => ({
          kind: 'card',
          id: toolCallId,
          name: part?.kind === 'tool' ? part.name : String(mastra.toolName ?? ''),
          card,
          answer: null,
          interruptId: interrupt.id
        }))
      }, finished)
    }
    case 'RUN_ERROR':
      return { ...turn, status: 'failed', error: String(event.message ?? ''), endedAt: now }
    default:
      return turn
  }
}

export const answerCard = (turns: readonly ShellTurn[], interruptId: string, answer: ChatAnswer): ShellTurn[] =>
  turns.map(turn =>
    turn.role !== 'agent'
      ? turn
      : {
          ...turn,
          parts: turn.parts.map(part =>
            part.kind === 'card' && part.interruptId === interruptId ? { ...part, answer, interruptId: null, answering: true } : part
          )
        }
  )

export const cardStateOf = (part: CardPart) => {
  if (part.answer) return part.answer.approved ? 'approved' : 'changed'
  return part.interruptId ? 'open' : 'closed'
}

const cardsIn = (turns: readonly ShellTurn[]) =>
  turns.flatMap(turn => (turn.role === 'agent' ? turn.parts : [])).filter((part): part is CardPart => part.kind === 'card')

export const cardIn = (turns: readonly ShellTurn[], id: string) => cardsIn(turns).find(part => part.id === id) ?? null

export const cardsAfter = (turns: readonly ShellTurn[], id: string) => {
  const cards = cardsIn(turns)
  const at = cards.findIndex(part => part.id === id)
  return at < 0 ? [] : cards.slice(at + 1)
}

// A card answered with a correction is followed by its revision: the next card of its kind.
export const revisionOf = (turns: readonly ShellTurn[], id: string) => {
  const cards = cardsIn(turns)
  const at = cards.findIndex(part => part.id === id)
  const card = cards[at]
  return card && cardStateOf(card) === 'changed' ? (cards.slice(at + 1).find(part => part.card.kind === card.card.kind) ?? null) : null
}

// Only the newest agent turn can be waiting on a card.
export const waitingIn = (turns: readonly ShellTurn[]) => {
  const turn = turns.findLast(entry => entry.role === 'agent')
  const part = turn?.role === 'agent' ? turn.parts.find(entry => entry.kind === 'card' && entry.interruptId) : undefined
  return part?.kind === 'card' ? part.interruptId : null
}

export const textOf = (turn: AgentTurn) =>
  turn.parts
    .flatMap(part => (part.kind === 'text' ? [part.text.trim()] : []))
    .filter(Boolean)
    .join('\n\n')
